import { useState } from 'react'
import { Card, Tabs, Title, Text } from '@nimbus-ds/components'
import { StatsIcon, MoneyIcon } from '@nimbus-ds/icons'
import { FunnelConfiguration } from '@/components/funnel/FunnelConfiguration'
import { GA4StatusCard } from '@/components/analytics/GA4StatusCard'
import { GA4ConfigurationGuide } from '@/components/analytics/GA4ConfigurationGuide'
import { BundleBootstrapCard } from '@/components/settings/BundleBootstrapCard'
import { RiskDefaultCard } from '@/components/settings/RiskDefaultCard'
import { useAuth } from '@/hooks/useAuth'
import type { FunnelConfig } from '@/types'

export function SettingsPage() {
  const { user } = useAuth()
  const [funnelConfig, setFunnelConfig] = useState<FunnelConfig | undefined>()

  const handleFunnelConfigChange = (config: FunnelConfig) => {
    setFunnelConfig(config)
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Title as="h1">Configuración</Title>
          <Text>
            Gestiona la configuración de tu tienda y funnels
          </Text>
        </div>
      </div>

      {/* Settings Tabs */}
      <Tabs>
        <Tabs.Item label="Funnel">
          <div className="mt-6">
            <FunnelConfiguration
              onConfigChange={handleFunnelConfigChange}
              initialConfig={funnelConfig}
            />
          </div>
        </Tabs.Item>

        <Tabs.Item label="Instalación">
          <div className="mt-6">
            {user?.store_id && <BundleBootstrapCard storeId={user.store_id} />}
          </div>
        </Tabs.Item>

        <Tabs.Item label="Analytics">
          <div className="mt-6 space-y-6">
            <Card>
              <Card.Header>
                <div className="flex items-center gap-2">
                  <StatsIcon size={20} />
                  <Title as="h3">Configuración de Analytics</Title>
                </div>
                <Text>
                  Conecta tus herramientas de analítica para obtener datos precisos de conversión
                </Text>
              </Card.Header>
              <Card.Body>
                {user?.store_id && (
                  <GA4StatusCard storeId={user.store_id} />
                )}
              </Card.Body>
            </Card>

            {/* GA4 Configuration Guide */}
            {user?.store_id && (
              <GA4ConfigurationGuide storeId={user.store_id} />
            )}
          </div>
        </Tabs.Item>

        <Tabs.Item label="Decisiones">
          <div className="mt-6">
            {user?.store_id && <RiskDefaultCard storeId={user.store_id} />}
          </div>
        </Tabs.Item>

        <Tabs.Item label="Métricas de Negocio">
          <div className="mt-6 space-y-6">
            <Card>
              <Card.Header>
                <div className="flex items-center gap-2">
                  <MoneyIcon size={20} />
                  <Title as="h3">Métricas de Negocio</Title>
                </div>
                <Text>
                  Configura las métricas base de tu tienda para análisis ROI automático en experimentos
                </Text>
              </Card.Header>
              <Card.Body>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Visitantes mensuales promedio</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="50,000"
                      />
                      <Text fontSize="caption">
                        Número de visitantes únicos por mes
                      </Text>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Tasa de conversión actual (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="2.5"
                      />
                      <Text fontSize="caption">
                        Porcentaje de visitantes que realizan una compra
                      </Text>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Ticket promedio (AOV)</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="120"
                      />
                      <Text fontSize="caption">
                        Valor promedio de orden en tu moneda local
                      </Text>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Lifetime Value (CLV) - Opcional</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="300"
                      />
                      <Text fontSize="caption">
                        Valor total que genera un cliente a lo largo del tiempo
                      </Text>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Costo de adquisición (CAC) - Opcional</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="25"
                      />
                      <Text fontSize="caption">
                        Costo promedio para adquirir un nuevo cliente
                      </Text>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Margen de ganancia (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="40"
                      />
                      <Text fontSize="caption">
                        Porcentaje de ganancia después de costos
                      </Text>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">💡 ¿Cómo obtener estos datos?</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• <strong>Visitantes:</strong> Google Analytics → Audiencia → Resumen</li>
                    <li>• <strong>Conversión:</strong> TiendaNube Admin → Reportes → Conversiones</li>
                    <li>• <strong>AOV:</strong> TiendaNube Admin → Reportes → Ventas promedio</li>
                    <li>• <strong>CLV:</strong> Ingresos totales ÷ Número de clientes únicos</li>
                  </ul>
                </div>

                <div className="mt-6 flex justify-end">
                  <button className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90">
                    Guardar Configuración
                  </button>
                </div>
              </Card.Body>
            </Card>
          </div>
        </Tabs.Item>

      </Tabs>
    </div>
  )
}
